/**
 * Guesses what a cell holds: the opcode of a standard message, a text
 * comment, an address, and the fields of the well-known token and NFT
 * messages. Best effort — a hint is dropped the moment the bits don't fit.
 */

import { Address } from '~/tonweb.js';
import { bytesToHex, parseBocString } from '~/cell.js';

/* eslint no-bitwise: "off" */

// Fields: [name, type, ...args]. Types: uint(bits), coins, address, maybeRef,
// either (Cell or ^Cell — the forward_payload shape).
const KNOWN_OPCODES = Object.freeze({
    0x00000000: { name: 'text_comment', std: 'comment' },
    0x2167da4b: { name: 'encrypted_comment', std: 'comment' },
    0x0f8a7ea5: {
        name: 'jetton_transfer',
        std: 'TEP-74',
        fields: [['query_id', 'uint', 64], ['amount', 'coins'], ['destination', 'address'], ['response_destination', 'address'], ['custom_payload', 'maybeRef'], ['forward_ton_amount', 'coins'], ['forward_payload', 'either']],
    },
    0x178d4519: {
        name: 'jetton_internal_transfer',
        std: 'TEP-74',
        fields: [['query_id', 'uint', 64], ['amount', 'coins'], ['from', 'address'], ['response_address', 'address'], ['forward_ton_amount', 'coins'], ['forward_payload', 'either']],
    },
    0x7362d09c: {
        name: 'jetton_notify',
        std: 'TEP-74',
        fields: [['query_id', 'uint', 64], ['amount', 'coins'], ['sender', 'address'], ['forward_payload', 'either']],
    },
    0x595f07bc: {
        name: 'jetton_burn',
        std: 'TEP-74',
        fields: [['query_id', 'uint', 64], ['amount', 'coins'], ['response_destination', 'address'], ['custom_payload', 'maybeRef']],
    },
    0x7bdd97de: {
        name: 'jetton_burn_notification',
        std: 'TEP-74',
        fields: [['query_id', 'uint', 64], ['amount', 'coins'], ['sender', 'address'], ['response_destination', 'address']],
    },
    0xd53276db: { name: 'excesses', std: 'TEP-74', fields: [['query_id', 'uint', 64]] },
    0x5fcc3d14: {
        name: 'nft_transfer',
        std: 'TEP-62',
        fields: [['query_id', 'uint', 64], ['new_owner', 'address'], ['response_destination', 'address'], ['custom_payload', 'maybeRef'], ['forward_amount', 'coins'], ['forward_payload', 'either']],
    },
    0x05138d91: {
        name: 'nft_ownership_assigned',
        std: 'TEP-62',
        fields: [['query_id', 'uint', 64], ['prev_owner', 'address'], ['forward_payload', 'either']],
    },
    0x2fcb26a2: { name: 'nft_get_static_data', std: 'TEP-62', fields: [['query_id', 'uint', 64]] },
    0x8b771735: { name: 'nft_report_static_data', std: 'TEP-62', fields: [['query_id', 'uint', 64], ['index', 'uint', 256], ['collection', 'address']] },
    0x693d3950: { name: 'nft_get_royalty_params', std: 'TEP-66', fields: [['query_id', 'uint', 64]] },
    0xa8cb00ad: { name: 'nft_report_royalty_params', std: 'TEP-66', fields: [['query_id', 'uint', 64], ['numerator', 'uint', 16], ['denominator', 'uint', 16], ['destination', 'address']] },
    0x7369676e: { name: 'wallet_v5_signed_external', std: 'wallet v5' },
    0x73696e74: { name: 'wallet_v5_signed_internal', std: 'wallet v5' },
    0x63896e74: { name: 'tg_wallet_send_one_internal', std: 'tg wallet' },
    0x63896e75: { name: 'tg_wallet_send_one_external', std: 'tg wallet' },
    0x73896e74: { name: 'tg_wallet_send_bulk_internal', std: 'tg wallet' },
    0x73896e75: { name: 'tg_wallet_send_bulk_external', std: 'tg wallet' },
    0xfbba99c7: { name: 'tg_wallet_change_key_internal', std: 'tg wallet' },
    0xfbba99c8: { name: 'tg_wallet_change_key_external', std: 'tg wallet' },
    0x0ec3c86d: { name: 'action_send_msg', std: 'out_list' },
    0xffffffff: { name: 'bounced', std: 'bounce' },
});

const opcodeHex = op => `0x${op.toString(16).padStart(8, '0')}`;

// A request to a Telegram wallet starts with a 512-bit signature; the opcode
// comes after it, followed by subwallet id, valid until and seqno.
const TG_WALLET_SIGNATURE_BITS = 512;
const TG_WALLET_OPCODES = Object.freeze([0x63896e74, 0x63896e75, 0x73896e74, 0x73896e75, 0xfbba99c7, 0xfbba99c8]);

/**
 * The real opcode of a Telegram wallet request, or null when the cell is
 * not shaped like one.
 *
 * @param  {Cell} cell
 * @return {Number|null}
 */
const readTgWalletOpcode = function (cell) {
    if (!cell || cell.bits.length < TG_WALLET_SIGNATURE_BITS + 32) {
        return null;
    }

    const opcode = parseInt(cell.bits.substr(TG_WALLET_SIGNATURE_BITS, 32), 2);

    return TG_WALLET_OPCODES.includes(opcode) ? opcode : null;
};

/**
 * Message opcode as the explorer should print it for a message to a Telegram
 * wallet: the raw 32 bits are a slice of the signature.
 *
 * @param  {String} bodyBoc  base64 body
 * @return {String|null} 0x… or null when the body is not a signed request
 */
export const tgWalletOpcode = function (bodyBoc) {
    try {
        const { roots } = parseBocString(bodyBoc);
        const opcode = readTgWalletOpcode(roots[0]);

        return opcode === null ? null : opcodeHex(opcode);
    } catch (e) {
        return null;
    }
};

const friendly = ({ wc, hashPart }) => new Address(`${wc}:${bytesToHex(hashPart)}`).toString({ userFriendly: true, bouncable: true, testOnly: false });

const readField = function (slice, type, arg) {
    switch (type) {
        case 'uint': return slice.loadUintBig(arg).toString();
        case 'coins': return slice.loadCoins().toString();
        case 'address': {
            const address = slice.loadAddress();
            return address ? friendly(address) : 'addr_none';
        }
        case 'maybeRef': return slice.loadMaybeRef() ? '^cell' : 'nothing';
        case 'either': {
            if (slice.loadBit()) {
                slice.loadRef();
                return '^cell';
            }
            return slice.remainingBits > 0 ? `${slice.remainingBits} bits inline` : 'empty';
        }
        default: throw new Error(`Unknown field type ${type}`);
    }
};

// eslint-disable-next-line no-control-regex
const isValidText = text => text.length > 0 && !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\ufffd]/.test(text);

/**
 * @param  {Cell} cell
 * @return {Object} { opcode, opcodeName, standard, comment, address, fields, exotic }
 */
export const describeCell = function (cell) {
    const hints = {};

    if (cell.exotic) {
        const types = { 1: 'pruned branch', 2: 'library reference', 3: 'merkle proof', 4: 'merkle update' };
        hints.exotic = types[cell.exoticType] || `exotic type ${cell.exoticType}`;
        return hints;
    }

    if (cell.bits.length === 0 && cell.refs.length === 0) {
        hints.empty = true;
        return hints;
    }

    // A bare address (the shape get-methods return):
    if (cell.bits.length === 267 && cell.bits.startsWith('100')) {
        try {
            const address = cell.beginParse().loadAddress();
            if (address) hints.address = friendly(address);
        } catch (e) { /* not an address then */ }
    }

    if (cell.bits.length < 32) {
        return hints;
    }

    // A signed request to a Telegram wallet: the opcode sits behind the
    // signature, and the header after it is worth showing.
    const tgOpcode = readTgWalletOpcode(cell);
    if (tgOpcode !== null) {
        const slice = cell.beginParse();
        slice.loadBits(TG_WALLET_SIGNATURE_BITS + 32);
        hints.opcode = opcodeHex(tgOpcode);
        hints.opcodeName = KNOWN_OPCODES[tgOpcode].name;
        hints.standard = KNOWN_OPCODES[tgOpcode].std;
        try {
            hints.fields = [
                { name: 'subwallet_id', value: readField(slice, 'uint', 32) },
                { name: 'valid_until', value: readField(slice, 'uint', 32) },
                { name: 'seqno', value: readField(slice, 'uint', 32) },
            ];
        } catch (e) { /* header didn't fit, the opcode alone is still a hint */ }
        return hints;
    }
    const slice = cell.beginParse();
    const opcode = slice.loadUint(32);
    const known = KNOWN_OPCODES[opcode];

    hints.opcode = opcodeHex(opcode);

    if (known) {
        hints.opcodeName = known.name;
        hints.standard = known.std;
    }

    if (opcode === 0 && slice.remainingBits % 8 === 0) {
        try {
            const text = slice.loadStringTail();
            if (isValidText(text)) hints.comment = text;
        } catch (e) { /* not a comment */ }
        return hints;
    }

    if (known?.fields) {
        const fields = [];
        try {
            known.fields.forEach(([name, type, arg]) => {
                fields.push({ name, value: readField(slice, type, arg) });
            });
            hints.fields = fields;
        } catch (e) {
            // The layout didn't fit: keep the fields that did parse, marked partial.
            if (fields.length) {
                hints.fields = fields;
                hints.partial = true;
            }
        }
    }

    return hints;
};
